<template>
    <div class="pdfIcons">
        <button
            v-if="invoice.dunningPdfs && Object.keys(invoice.dunningPdfs).length > 0"
            class="kgr-table__action"
            :disabled="!invoice.pdfExists"
            style="color: red;"
            @click="downloadDunningPdfCollection(invoice)"
        >
            <kgr-icon
                :title="$t('Rechnung und Mahndokumente in einer Datei herunterladen')"
                :disabled="!invoice.pdfExists"
                icon-class="pdf"
            />
        </button>
        <button
            class="kgr-table__action"
            :disabled="!invoice.pdfExists"
            style="position: relative;"
            @click="downloadPdf()"
        >
            <kgr-icon
                :title="downloadPdfTitle"
                :disabled="!invoice.pdfExists"
                icon-class="pdf"
            />
            <i
                v-if="pdfLastDownload"
                class="kgr-indicator is-success dot"
            >
            </i>
        </button>
        <button
            :disabled="!invoice.pdfExists"
            @click.prevent.stop="previewPdf()"
        >
            <kgr-icon
                :title="previewPdfTitle"
                :disabled="!invoice.pdfExists"
                icon-class="preview"
            />
        </button>
        <button
            v-if="xInvoiceEnabled"
            :disabled="!invoice.xInvoiceExists"
            @click="downloadXInvoice()"
        >
            <kgr-icon
                :title="downloadXInvoiceTitle"
                :disabled="!invoice.xInvoiceExists"
                icon-class="arrow-downward"
            />
        </button>

        <kgr-generic-modal
            v-if="dialogPreviewPDF?.show"
            :value="dialogPreviewPDF?.show"
            :headline="$t('Das PDF wurde erstellt')"
            @close="dialogPreviewPDF.show = false"
        >
            <div class="kgr-readingBlock">
                <a
                    class="kgr-link"
                    :href="dialogPreviewPDF.url"
                    target="_blank"
                >
                    {{ dialogPreviewPDF.title }}
                </a>
            </div>
        </kgr-generic-modal>
    </div>
</template>

<script>
import ClubInvoice from '../../../js/dto/ClubInvoice.js';
import ClubInvoiceRepository from '../../../js/repository/ClubInvoiceRepository.js';
import ClubReverseInvoice from '../../../js/dto/ClubReverseInvoice.js';
import GenericModal from '../../common/modal/GenericModal.vue';
import Helper from '../../../js/accounting/Helper.js';
import Icon from '../../common/icon/Icon.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-generic-modal': GenericModal
    },
    props: {
        invoice: {
            type: [ClubInvoice, ClubReverseInvoice],
            required: true,
        }
    },
    data() {
        return {
            pdfLastDownload: this.invoice.pdfLastDownload,
            dialogPreviewPDF: {
                show: false,
                url: '',
            }
        }
    },
    computed: {
        downloadPdfTitle() {
            if (!this.invoice.pdfExists) {
                return this.$t('Das PDF wird erstellt');
            }

            let title = 'PDF herunterladen';

            if (this.pdfLastDownload) {
                title = title + '\n\nLetzter Download von einem Mitarbeiter am ' + this.formatDate(this.pdfLastDownload);
            }

            if (this.invoice.pdfLastDownloadByMember) {
                title = title + '\n\nLetzter Download vom Rechnungsempfänger am ' + this.formatDate(this.invoice.pdfLastDownloadByMember);
            }

            return title
        },
        previewPdfTitle() {
            if (!this.invoice.pdfExists) {
                return this.$t('Das PDF wird erstellt');
            }
            return this.$t('PDF ansehen')
        },
        downloadXInvoiceTitle() {
            return this.$t('XRechnung herunterladen');
        },
        pdfUrl() {
            let url = '/club/club-invoice-pdfs/';
            if (this.invoice.isReverseInvoice) {
                url = '/club/club-reverse-invoice-pdfs/';
            }
            url += this.invoice.id + '/download-history';
            return url;
        },
        xInvoiceEnabled() {
            // #9295 XRechnung is not enabled yet
            return false;
        }
    },
    methods: {
        downloadDunningPdfCollection(invoice) {
            const url = `/club/club-invoice-dunning-pdfs/${invoice.id}/download-history/collection`;
            axios.get(url, {responseType: 'blob'})
                .then(response => {
                    let fileName = response.headers['content-disposition'].split('filename=')[1].replace(/"/g, '');
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download',  fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    this.setLastDownloadDate();
                });
        },
        downloadPdf() {
            axios.post(this.pdfUrl)
                .then(response => {
                    axios.get(response.headers.location, {responseType: 'blob'})
                        .then(response => {
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', this.invoice.number + '.pdf');
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            this.setLastDownloadDate();
                        });
                });
        },
        downloadXInvoice() {
            ClubInvoiceRepository.getXInvoiceByInvoiceId(this.invoice.id, this.invoice instanceof ClubReverseInvoice)
                .then(() => {})
                .catch(() => this.$notify.error('error.loadingData'));
        },
        setLastDownloadDate() {
            this.pdfLastDownload = new Helper().today();
        },
        formatDate(date) {
            return new Helper().formatDate(date);
        },
        async previewPdf() {
            let pdfWindow
            try{
                pdfWindow = window.open('', '_blank')

                if (pdfWindow) {
                    pdfWindow.document.title = this.$t('Das PDF wird erstellt')
                }

                const response = await axios.post(this.pdfUrl)
                const locationHeader = response && response.headers && response.headers.location;
                if (!locationHeader) {
                    throw new Error('Missing PDF location header');
                }
                const pdfUrl = response.headers.location + '?contentDisposition=inline'

                this.setLastDownloadDate()

                if (pdfWindow) {
                    pdfWindow.location = pdfUrl
                } else {
                    this.dialogPreviewPDF.show = true
                    this.dialogPreviewPDF.title = this.$t('PDF der Rechnung #{number} öffnen', { number: this.invoice.number })
                    this.dialogPreviewPDF.url = pdfUrl
                }

            } catch(error) {
                if (pdfWindow && !pdfWindow.closed) {
                    pdfWindow.close();
                }

                console.warn('could not fetch pdf url', error)
                this.$notify.error('error.loadingData')
            }
        },
    },
}
</script>

<style>
    div.pdfIcons {
        display: inline-block;
        margin-left: 15px;
    }
    .dot {
        position: absolute;
        top: -3px;
        right: -3px;
    }
</style>
